// Camada de serviço do domínio notification-center.
// TODAS as chamadas HTTP do domínio vivem aqui — páginas e
// hooks nunca chamam `api.get` diretamente.
import { api } from "@/api"
import { ENDPOINTS } from "@/constants"
import type {
  NotificationCenterOverviewParamsType,
  NotificationCenterOverviewResponseType,
  NotificationCenterWhiteLabelsConfigurationsResponseType,
  NotificationCenterWhiteLabelsResponseType,
  NotificationCenterWhiteLabelsTemplatesResponseType,
  NotificationChannelProviderWritePayloadType,
  NotificationConfigurationWriteAckType,
  NotificationDetailType,
  NotificationEventTemplateWritePayloadType,
  NotificationEventUpdatePayloadType,
  NotificationOriginTransactionType,
  NotificationResendResultType,
  NotificationSchedulerWritePayloadType,
  NotificationSearchChannelType,
  NotificationSearchFiltersType,
  NotificationSearchResponseType,
  NotificationTemplateAiImprovePayloadType,
  NotificationTemplateAiImproveResultType,
  NotificationTemplateCreatePayloadType,
  NotificationTemplateCreateResultType,
  NotificationTemplateEventsResponseType,
  NotificationTemplateTestSendPayloadType,
  NotificationTemplateTestSendResultType,
  NotificationTemplateVersionsResponseType,
  NotificationTemplateVersionWritePayloadType,
  NotificationWhiteLabelConfigurationPayloadType,
  NotificationWhiteLabelEventsResponseType,
  NotificationWhiteLabelProvidersResponseType,
  NotificationZenviaCredentialsPayloadType,
  NotificationZenviaJourneyWritePayloadType,
  NotificationZenviaTestResultType,
} from "@/types/notification-center"
import { StringHelper } from "@/utils"

// Filtros da listagem de templates (valores de querystring).
export interface NotificationCenterTemplatesFilters {
  enabled?: string
  active?: string
  channel?: string
}

/**
 * Lista na raiz da resposta.
 *
 * Quando o backend devolve `[]`, o gateway (heimdall) responde 204 SEM
 * corpo e o axios entrega `""` — corpo vazio é lista vazia. Corpo com
 * outro formato REJEITA: virar `[]` diria "nada cadastrado" (em white
 * labels, a tela ofereceria recriar configurações que já existem).
 */
const toRootList = <T>(
  data: unknown,
  endpoint: string
): T[] => {
  if (data === "" || data === null || data === undefined)
    return []
  if (!Array.isArray(data)) {
    throw new Error(
      `GET ${endpoint}: resposta não é uma lista`
    )
  }
  return data as T[]
}

/** GET /notification-center/white-labels → WLs que já têm configuração. */
export const getNotificationCenterWhiteLabels =
  async (): Promise<NotificationCenterWhiteLabelsResponseType> => {
    const { data } = await api.get<unknown>(
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS
    )
    return toRootList(
      data,
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS
    )
  }

// GET /notification-center/white-labels/:id/configurations.
export const getNotificationCenterWhiteLabelConfigurations =
  async (
    whiteLabelId: string
  ): Promise<NotificationCenterWhiteLabelsConfigurationsResponseType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_CONFIGURATIONS.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      )
    const { data } =
      await api.get<NotificationCenterWhiteLabelsConfigurationsResponseType>(
        url
      )
    return data
  }

// GET /notification-center/white-labels/:id/templates?enabled=&active=&channel=
// → array raiz.
export const getNotificationCenterWhiteLabelTemplates =
  async (
    whiteLabelId: string,
    filters: NotificationCenterTemplatesFilters = {}
  ): Promise<NotificationCenterWhiteLabelsTemplatesResponseType> => {
    const queryString = StringHelper.objectToQueryString({
      enabled: filters.enabled,
      active: filters.active,
      channel: filters.channel,
    })
    const url = `${ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_TEMPLATES.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    )}?${queryString}`
    const { data } = await api.get<unknown>(url)
    return toRootList(data, url)
  }

// GET /notification-center/white-labels/:id/events — configuração
// rica do WL (§4.3): mailFrom + canais/providers + eventos com
// templates por canal (imediato) e agendamentos.
export const getNotificationCenterWhiteLabelEvents = async (
  whiteLabelId: string
): Promise<NotificationWhiteLabelEventsResponseType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_EVENTS.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    )
  const { data } =
    await api.get<NotificationWhiteLabelEventsResponseType>(
      url
    )
  return data
}

// POST …/configurations/default — cria a configuração padrão do WL
// (eventos padrão do catálogo com os templates padrão do sistema).
export const postNotificationCenterDefaultConfiguration =
  async (
    whiteLabelId: string
  ): Promise<NotificationConfigurationWriteAckType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_CONFIGURATIONS_DEFAULT.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      )
    const { data } =
      await api.post<NotificationConfigurationWriteAckType>(
        url
      )
    return data
  }

// PATCH …/configuration — altera dados gerais da configuração do WL
// (hoje: o remetente/mailFrom). O fluxo é criar com o padrão e
// alterar depois. Devolve o write-ack padrão.
export const patchNotificationCenterWhiteLabelConfiguration =
  async (
    whiteLabelId: string,
    payload: NotificationWhiteLabelConfigurationPayloadType
  ): Promise<NotificationConfigurationWriteAckType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_CONFIGURATION.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      )
    const { data } =
      await api.patch<NotificationConfigurationWriteAckType>(
        url,
        payload
      )
    return data
  }

// Anexa `?transactionType=` às escritas de UM evento quando o card
// sabe a linha alvo (ORDER/SUBSCRIPTION). Ausente = comportamento
// antigo do gateway (altera TODAS as linhas do evento).
const withEventTransactionType = (
  url: string,
  transactionType?: NotificationOriginTransactionType
) =>
  transactionType
    ? `${url}?transactionType=${encodeURIComponent(transactionType)}`
    : url

// PATCH …/events/:eventName — habilita/desabilita o evento no WL.
export const patchNotificationCenterWhiteLabelEvent =
  async (
    whiteLabelId: string,
    eventName: string,
    payload: NotificationEventUpdatePayloadType,
    transactionType?: NotificationOriginTransactionType
  ): Promise<NotificationConfigurationWriteAckType> => {
    const url = withEventTransactionType(
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_EVENT.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      ).replace("{{eventName}}", eventName),
      transactionType
    )
    const { data } =
      await api.patch<NotificationConfigurationWriteAckType>(
        url,
        payload
      )
    return data
  }

// POST …/events/:eventName/schedulers — cria agendamento.
export const postNotificationCenterEventScheduler = async (
  whiteLabelId: string,
  eventName: string,
  payload: NotificationSchedulerWritePayloadType,
  transactionType?: NotificationOriginTransactionType
): Promise<NotificationConfigurationWriteAckType> => {
  const url = withEventTransactionType(
    ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_EVENT_SCHEDULERS.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    ).replace("{{eventName}}", eventName),
    transactionType
  )
  const { data } =
    await api.post<NotificationConfigurationWriteAckType>(
      url,
      payload
    )
  return data
}

// PUT …/events/:eventName/templates — associa/troca o template do
// envio imediato de UM canal do evento.
export const putNotificationCenterEventImmediateTemplate =
  async (
    whiteLabelId: string,
    eventName: string,
    payload: NotificationEventTemplateWritePayloadType,
    transactionType?: NotificationOriginTransactionType
  ): Promise<NotificationConfigurationWriteAckType> => {
    const url = withEventTransactionType(
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_EVENT_TEMPLATES.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      ).replace("{{eventName}}", eventName),
      transactionType
    )
    const { data } =
      await api.put<NotificationConfigurationWriteAckType>(
        url,
        payload
      )
    return data
  }

// Interpola a URL de um agendamento específico (PUT/DELETE).
const buildEventSchedulerUrl = (
  whiteLabelId: string,
  eventName: string,
  schedulerUuid: string,
  transactionType?: NotificationOriginTransactionType
) =>
  withEventTransactionType(
    ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_EVENT_SCHEDULER.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    )
      .replace("{{eventName}}", eventName)
      .replace("{{schedulerUuid}}", schedulerUuid),
    transactionType
  )

// PUT …/events/:eventName/schedulers/:uuid — atualiza agendamento.
export const putNotificationCenterEventScheduler = async (
  whiteLabelId: string,
  eventName: string,
  schedulerUuid: string,
  payload: NotificationSchedulerWritePayloadType,
  transactionType?: NotificationOriginTransactionType
): Promise<NotificationConfigurationWriteAckType> => {
  const { data } =
    await api.put<NotificationConfigurationWriteAckType>(
      buildEventSchedulerUrl(
        whiteLabelId,
        eventName,
        schedulerUuid,
        transactionType
      ),
      payload
    )
  return data
}

// DELETE …/events/:eventName/schedulers/:uuid — remove agendamento.
export const deleteNotificationCenterEventScheduler =
  async (
    whiteLabelId: string,
    eventName: string,
    schedulerUuid: string,
    transactionType?: NotificationOriginTransactionType
  ): Promise<NotificationConfigurationWriteAckType> => {
    const { data } =
      await api.delete<NotificationConfigurationWriteAckType>(
        buildEventSchedulerUrl(
          whiteLabelId,
          eventName,
          schedulerUuid,
          transactionType
        )
      )
    return data
  }

// GET /notification-center/white-labels/:id/providers — Provedores
// do WL: associação canal→provider + catálogo global + config Zenvia
// (credenciais write-only e journeys).
export const getNotificationCenterWhiteLabelProviders =
  async (
    whiteLabelId: string
  ): Promise<NotificationWhiteLabelProvidersResponseType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_ID_PROVIDER.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      )
    const { data } =
      await api.get<NotificationWhiteLabelProvidersResponseType>(
        url
      )
    return data
  }

// PUT …/providers/:channel — associa provider e liga/desliga o canal.
export const putNotificationCenterChannelProvider = async (
  whiteLabelId: string,
  channel: NotificationSearchChannelType,
  payload: NotificationChannelProviderWritePayloadType
): Promise<NotificationConfigurationWriteAckType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_PROVIDER_CHANNEL.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    ).replace("{{channel}}", channel)
  const { data } =
    await api.put<NotificationConfigurationWriteAckType>(
      url,
      payload
    )
  return data
}

// PUT …/providers/zenvia/credentials — clientSecret write-only:
// SEMPRE um valor novo digitado; o valor atual nunca trafega de volta.
export const putNotificationCenterZenviaCredentials =
  async (
    whiteLabelId: string,
    payload: NotificationZenviaCredentialsPayloadType
  ): Promise<NotificationConfigurationWriteAckType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_PROVIDERS_ZENVIA_CREDENTIALS.replace(
        "{{whiteLabelId}}",
        whiteLabelId
      )
    const { data } =
      await api.put<NotificationConfigurationWriteAckType>(
        url,
        payload
      )
    return data
  }

// POST …/providers/zenvia/journeys — mapeia evento → journey.
export const postNotificationCenterZenviaJourney = async (
  whiteLabelId: string,
  payload: NotificationZenviaJourneyWritePayloadType
): Promise<NotificationConfigurationWriteAckType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_PROVIDERS_ZENVIA_JOURNEYS.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    )
  const { data } =
    await api.post<NotificationConfigurationWriteAckType>(
      url,
      payload
    )
  return data
}

// Interpola a URL de uma journey específica (PUT/DELETE) — o path
// param é o id do REGISTRO (ZPJ-…), não o journeyId da Zenvia.
const buildZenviaJourneyUrl = (
  whiteLabelId: string,
  journeyRecordId: string
) =>
  ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_PROVIDERS_ZENVIA_JOURNEY.replace(
    "{{whiteLabelId}}",
    whiteLabelId
  ).replace("{{journeyId}}", journeyRecordId)

// PUT …/providers/zenvia/journeys/:journeyId — atualiza o mapeamento.
export const putNotificationCenterZenviaJourney = async (
  whiteLabelId: string,
  journeyRecordId: string,
  payload: NotificationZenviaJourneyWritePayloadType
): Promise<NotificationConfigurationWriteAckType> => {
  const { data } =
    await api.put<NotificationConfigurationWriteAckType>(
      buildZenviaJourneyUrl(whiteLabelId, journeyRecordId),
      payload
    )
  return data
}

// DELETE …/providers/zenvia/journeys/:journeyId — remove o mapeamento.
export const deleteNotificationCenterZenviaJourney = async (
  whiteLabelId: string,
  journeyRecordId: string
): Promise<NotificationConfigurationWriteAckType> => {
  const { data } =
    await api.delete<NotificationConfigurationWriteAckType>(
      buildZenviaJourneyUrl(whiteLabelId, journeyRecordId)
    )
  return data
}

// POST …/providers/zenvia/test — testa a conexão com as credenciais
// salvas (zenvia-test-result.json).
export const postNotificationCenterZenviaTest = async (
  whiteLabelId: string
): Promise<NotificationZenviaTestResultType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_PROVIDERS_ZENVIA_TEST.replace(
      "{{whiteLabelId}}",
      whiteLabelId
    )
  const { data } =
    await api.post<NotificationZenviaTestResultType>(url)
  return data
}

// POST /notification-center/templates — cria o template (nasce com
// v1 DRAFT vazia) e devolve o uuid REAL (template-create-result).
export const postNotificationCenterTemplate = async (
  payload: NotificationTemplateCreatePayloadType
): Promise<NotificationTemplateCreateResultType> => {
  const { data } =
    await api.post<NotificationTemplateCreateResultType>(
      ENDPOINTS.NOTIFICATION_CENTER_TEMPLATES,
      payload
    )
  return data
}

// GET /notification-center/templates/:templateUuid/versions —
// template com variables/usedBy + versões (lifecycle DRAFT/
// PUBLISHED/ARCHIVED, exatamente 1 active).
export const getNotificationCenterTemplateVersions = async (
  templateUuid: string
): Promise<NotificationTemplateVersionsResponseType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_VERSIONS.replace(
      "{{templateUuid}}",
      templateUuid
    )
  const { data } =
    await api.get<NotificationTemplateVersionsResponseType>(
      url
    )
  return data
}

// GET /notification-center/templates/:templateUuid/events — TODOS os
// eventos (GeneralConfigurationType) com a flag `associated` marcando
// os que o template já cobre. Alimenta o seletor de cenário do
// test-send (o front pré-marca os associados).
export const getNotificationCenterTemplateEvents = async (
  templateUuid: string
): Promise<NotificationTemplateEventsResponseType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_EVENTS.replace(
      "{{templateUuid}}",
      templateUuid
    )
  const { data } =
    await api.get<NotificationTemplateEventsResponseType>(
      url
    )
  return data
}

// POST …/templates/:uuid/versions — cria nova versão DRAFT.
export const postNotificationCenterTemplateVersion = async (
  templateUuid: string,
  payload: NotificationTemplateVersionWritePayloadType
): Promise<NotificationConfigurationWriteAckType> => {
  const url =
    ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_VERSIONS.replace(
      "{{templateUuid}}",
      templateUuid
    )
  const { data } =
    await api.post<NotificationConfigurationWriteAckType>(
      url,
      payload
    )
  return data
}

// PUT …/templates/:uuid/versions/:version/activate — ativa a
// versão (publicação e rollback usam o MESMO endpoint).
export const putNotificationCenterTemplateVersionActivate =
  async (
    templateUuid: string,
    version: number
  ): Promise<NotificationConfigurationWriteAckType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_VERSION_ACTIVATE.replace(
        "{{templateUuid}}",
        templateUuid
      ).replace("{{version}}", String(version))
    const { data } =
      await api.put<NotificationConfigurationWriteAckType>(
        url
      )
    return data
  }

// POST …/templates/:uuid/test-send — envio de teste real
// (marcado com [TESTE] pelo backend).
export const postNotificationCenterTemplateTestSend =
  async (
    templateUuid: string,
    payload: NotificationTemplateTestSendPayloadType
  ): Promise<NotificationTemplateTestSendResultType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_TEST_SEND.replace(
        "{{templateUuid}}",
        templateUuid
      )
    const { data } =
      await api.post<NotificationTemplateTestSendResultType>(
        url,
        payload
      )
    return data
  }

// POST …/templates/:uuid/ai-improve — pede à IA uma versão melhorada
// do source (com uma instrução opcional do usuário). Onda 2: o
// gateway responde um MOCK de contrato (template-ai-improve.json); a
// IA real vem depois no backend. O header x-white-label-uuid é
// injetado pelo interceptor (api-helper), como nas demais chamadas.
export const postNotificationCenterTemplateAiImprove =
  async (
    templateUuid: string,
    payload: NotificationTemplateAiImprovePayloadType
  ): Promise<NotificationTemplateAiImproveResultType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_AI_IMPROVE.replace(
        "{{templateUuid}}",
        templateUuid
      )
    const { data } =
      await api.post<NotificationTemplateAiImproveResultType>(
        url,
        payload
      )
    return data
  }

// GET /notification-center/notifications?… — busca paginada de
// envios (Consulta). Filtros indefinidos são omitidos da query.
export const getNotificationCenterNotifications = async (
  filters: NotificationSearchFiltersType = {}
): Promise<NotificationSearchResponseType> => {
  const queryString =
    StringHelper.objectToQueryString(filters)
  const url = `${ENDPOINTS.NOTIFICATION_CENTER_NOTIFICATIONS}?${queryString}`
  const { data } =
    await api.get<NotificationSearchResponseType>(url)
  return data
}

// GET /notification-center/notifications/:schedulerId — detalhe do
// ENVIO: campos comuns do disparo + TODOS os canais completos em
// channels[] (timeline, template enviado, erro) num payload único.
export const getNotificationCenterNotificationDetail =
  async (uuid: string): Promise<NotificationDetailType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_NOTIFICATION_DETAIL.replace(
        "{{uuid}}",
        uuid
      )
    const { data } =
      await api.get<NotificationDetailType>(url)
    return data
  }

// POST /notification-center/notifications/:uuid/resend — reenvia/
// reprocessa o envio (volta para a fila com WAITING_TO_RESEND).
export const postNotificationCenterNotificationResend =
  async (
    uuid: string
  ): Promise<NotificationResendResultType> => {
    const url =
      ENDPOINTS.NOTIFICATION_CENTER_NOTIFICATION_RESEND.replace(
        "{{uuid}}",
        uuid
      )
    const { data } =
      await api.post<NotificationResendResultType>(url)
    return data
  }

// GET /notification-center/dashboards/overview?… — dashboard
// intradiário (§4.1 da spec). date (yyyy-MM-dd, default hoje) +
// fromHour/toHour (HH:mm) opcionais; whiteLabelId ausente =
// "Todos". Params indefinidos são omitidos da query.
export const getNotificationCenterDashboardOverview =
  async (
    params: NotificationCenterOverviewParamsType = {}
  ): Promise<NotificationCenterOverviewResponseType> => {
    const queryString =
      StringHelper.objectToQueryString(params)
    const url = `${ENDPOINTS.NOTIFICATION_CENTER_DASHBOARD_OVERVIEW}?${queryString}`
    const { data } =
      await api.get<NotificationCenterOverviewResponseType>(
        url
      )
    return data
  }
